import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/common/PasswordInput';
import { Label } from '@/components/ui/label';
import { isWebAuthnAvailable } from '@/lib/webauthn';
import { Mountain, Loader2, ShieldCheck, ArrowLeft, Fingerprint } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function GoogleIcon() {
  return (<svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>);
}

function AppleIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>);
}

/**
 * Login riutilizzabile: identico alla LoginPage attuale (OAuth + 2FA),
 * parametrizzato per immagine, testi e link di registrazione.
 * Usa il tema globale (non lo scope .sila-public).
 */
export default function LoginScreen({
  bg,
  heroTitle = 'Vivi la Sila.',
  heroText = 'La piattaforma enterprise che connette territorio, imprese e visitatori.',
  badge = 'Accesso riservato',
  formTitle = 'Bentornato',
  formSubtitle = 'Accedi al tuo pannello di controllo.',
  showOAuth = true,
  register = null, // { to, label, prefix }
  register2 = null,
  testidPrefix = 'login',
}) {
  const { login, loginWithPasskey, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaStep, setMfaStep] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [pkLoading, setPkLoading] = useState(false);
  const [oauth, setOauth] = useState({ google: false, linkedin: false, apple: false });
  const pkSupported = isWebAuthnAvailable();

  useEffect(() => {
    if (showOAuth) api.get('/auth/oauth/status').then(({ data }) => setOauth(data)).catch(() => {});
    const pending = params.get('pending');
    const oerr = params.get('oauth_error');
    if (pending) setInfo(pending === 'pending' ? 'Accesso social riuscito. Il tuo account è in attesa di approvazione dell\'amministratore.' : `Accesso non consentito: account ${pending}.`);
    if (oerr) setError('Accesso social non riuscito. Riprova.');
  }, [params, showOAuth]);

  const destFor = (u) => {
    if (!u) return '/area';
    if (u.role === 'admin') return '/admin';
    if (u.role === 'staff') return '/staff-area';
    if (u.role === 'investor') return '/investitori';
    if (u.role === 'stakeholder' && u.tenant_type === 'guida') return '/area/parco';
    return '/area';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const u = await login(email, password, mfaStep ? code : undefined);
      navigate(destFor(u));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'MFA_REQUIRED') { setMfaStep(true); setError(''); }
      else if (detail === 'MFA_INVALID') { setError('Codice 2FA non valido.'); }
      else if (detail === 'EMAIL_NOT_VERIFIED') {
        try { await api.post('/auth/verify-email/resend', { email }); } catch { /* noop */ }
        navigate(`/verifica-email?email=${encodeURIComponent(email)}`);
      }
      else setError(formatApiError(detail) || err.message);
    } finally { setLoading(false); }
  };

  const social = (provider) => { window.location.href = `${API_BASE}/api/auth/oauth/${provider}/login`; };

  const passkeyLogin = async () => {
    setError(''); setPkLoading(true);
    try {
      const u = await loginWithPasskey(email || undefined);
      navigate(destFor(u));
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') { /* annullato */ }
      else setError(formatApiError(err.response?.data?.detail) || 'Accesso con passkey non riuscito');
    } finally { setPkLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 lg:block">
        <img src={bg} alt="Sila" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/85 via-[#0B1220]/35 to-transparent" />
        <Link to="/" className="absolute left-8 top-8 flex items-center gap-2 text-white/90 transition-colors hover:text-white" data-testid={`${testidPrefix}-back-home`}>
          <ArrowLeft className="h-4 w-4" /><span className="text-sm font-medium">Torna al sito</span>
        </Link>
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-display text-4xl font-bold leading-tight">{heroTitle}</h2>
          <p className="mt-3 max-w-md text-white/85">{heroText}</p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
            <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">{badge}</p></div>
          </div>

          {!mfaStep ? (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight">{formTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{formSubtitle}</p>

              {showOAuth && (oauth.google || oauth.linkedin || oauth.apple) && (
                <div className="mt-6 space-y-2">
                  {oauth.google && (
                    <Button type="button" variant="outline" className="w-full gap-2" onClick={() => social('google')} data-testid={`${testidPrefix}-google-button`}>
                      <GoogleIcon /> Continua con Google
                    </Button>
                  )}
                  {oauth.linkedin && (
                    <Button type="button" variant="outline" className="w-full gap-2" onClick={() => social('linkedin')} data-testid={`${testidPrefix}-linkedin-button`}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
                      Continua con LinkedIn
                    </Button>
                  )}
                  {oauth.apple && (
                    <Button type="button" variant="outline" className="w-full gap-2" onClick={() => social('apple')} data-testid={`${testidPrefix}-apple-button`}>
                      <AppleIcon /> Continua con Apple
                    </Button>
                  )}
                  <div className="relative py-2 text-center"><span className="bg-background px-2 text-xs text-muted-foreground">oppure con email</span><div className="absolute inset-x-0 top-1/2 -z-10 h-px bg-border" /></div>
                </div>
              )}

              {info && <p className="mt-4 rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground" data-testid={`${testidPrefix}-info`}>{info}</p>}

              <form onSubmit={submit} className="mt-4 space-y-4">
                <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" data-testid={`${testidPrefix}-email-input`} /></div>
                <div className="space-y-2"><Label htmlFor="password">Password</Label><PasswordInput id="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="********" data-testid={`${testidPrefix}-password-input`} toggleTestId={`${testidPrefix}-password-toggle`} /></div>
                {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid={`${testidPrefix}-error`}>{error}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid={`${testidPrefix}-form-submit-button`}>{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi</Button>
              </form>
              {pkSupported && (
                <Button type="button" variant="outline" className="mt-3 w-full gap-2" onClick={passkeyLogin} disabled={pkLoading} data-testid={`${testidPrefix}-passkey-button`}>
                  {pkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} Accedi con Passkey
                </Button>
              )}
              {register && (
                <p className="mt-6 text-center text-sm text-muted-foreground">{register.prefix} <Link to={register.to} className="font-medium text-primary hover:underline" data-testid={`${testidPrefix}-register-link`}>{register.label}</Link></p>
              )}
              {register2 && (
                <p className="mt-1 text-center text-sm text-muted-foreground">{register2.prefix} <Link to={register2.to} className="font-medium text-primary hover:underline" data-testid={`${testidPrefix}-register2-link`}>{register2.label}</Link></p>
              )}
            </>
          ) : (
            <>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-6 w-6 text-primary" /></div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Verifica in due passaggi</h1>
              <p className="mt-1 text-sm text-muted-foreground">Inserisci il codice a 6 cifre dalla tua app Authenticator (o un codice di recupero).</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="space-y-2"><Label htmlFor="code">Codice</Label><Input id="code" inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid={`${testidPrefix}-2fa-input`} /></div>
                {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid={`${testidPrefix}-error`}>{error}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid={`${testidPrefix}-2fa-submit`}>{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verifica e accedi</Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => { setMfaStep(false); setCode(''); setError(''); }}><ArrowLeft className="mr-1.5 h-4 w-4" />Torna indietro</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
